/**
 * Contexto de la configuración de presentación del tenant, y sus hooks.
 *
 * Separado de `TenantProvider.tsx` por la misma convención que `useApi.ts`:
 * un `.tsx` exporta componentes y nada más.
 */
import { createContext, useContext } from 'react';

import { DEFAULT_TENANT_SETTINGS, type TenantSettings } from './settings';

export interface TenantContextValue {
  settings: TenantSettings;
  /**
   * `false` mientras no se sepa cuál es la configuración real: sin sesión, o
   * con la sesión abierta pero el JSON del tenant todavía en vuelo.
   *
   * Importa para el idioma: si el andamio tomara los defaults como si fueran
   * del tenant, la pantalla de un analista brasileño pasaría de portugués (el
   * de su navegador) a español (el default) y recién después a portugués otra
   * vez. Dos parpadeos de idioma en el arranque. Ver `i18n/languages.ts`.
   */
  isResolved: boolean;
}

export const TenantContext = createContext<TenantContextValue>({
  settings: DEFAULT_TENANT_SETTINGS,
  isResolved: false,
});

/** Locale, huso, moneda y nombre visible del tenant de la sesión. */
export function useTenantSettings(): TenantSettings {
  return useContext(TenantContext).settings;
}

/** Lo mismo, más si la configuración ya se resolvió. Lo usa el `I18nProvider`. */
export function useTenantContext(): TenantContextValue {
  return useContext(TenantContext);
}
